
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FeedbackView from '@/components/FeedbackView';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/feedback-view/LoadingSpinner';

// Type pour le résultat de la fonction RPC check_feedback_access
interface AccessCheckResult {
  has_access: boolean;
}

const ViewFeedback = () => {
  const { feedbackId: id } = useParams<{ feedbackId: string }>();
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkFeedbackAccess = async () => {
      if (!id) {
        toast({
          title: "Erreur",
          description: "Identifiant de feedback manquant",
          variant: "destructive",
        });
        setLoading(false);
        navigate('/dashboard');
        return;
      }

      if (!user) {
        toast({
          title: "Non autorisé",
          description: "Veuillez vous connecter pour voir ce feedback",
          variant: "destructive",
        });
        setLoading(false);
        navigate('/auth');
        return;
      }

      try {
        console.log("Vérification de l'accès au feedback:", id, "Utilisateur:", user.id, "Email:", user.email);
        
        // Accès automatique pour les administrateurs
        if (profile?.role === 'admin') {
          console.log("Accès administrateur accordé automatiquement");
          setAuthorized(true);
          setLoading(false);
          return;
        }

        // Obtenir le feedback - approche de base
        const { data: feedback, error } = await supabase
          .from('feedbacks')
          .select('user_id, candidate_user_id, candidate_email, recruiter_email')
          .eq('id', id)
          .maybeSingle();

        if (error) {
          console.error("Erreur lors de la récupération du feedback:", error);
          toast({
            title: "Erreur",
            description: "Impossible de charger le feedback",
            variant: "destructive",
          });
          setAuthorized(false);
          setLoading(false);
          navigate('/dashboard');
          return;
        }

        if (!feedback) {
          console.log("Feedback non trouvé avec l'ID:", id);
          toast({
            title: "Feedback non trouvé",
            description: "Le feedback demandé n'existe pas",
            variant: "destructive",
          });
          setAuthorized(false);
          setLoading(false);
          navigate('/dashboard');
          return;
        }

        console.log("Feedback trouvé:", feedback);

        // Vérification d'accès du créateur
        if (feedback.user_id === user.id) {
          console.log("Accès accordé: créateur du feedback");
          setAuthorized(true);
          setLoading(false);
          return;
        }

        // Vérification d'accès du candidat direct (priorité 1)
        if (feedback.candidate_user_id === user.id) {
          console.log("Accès accordé: candidat direct (par ID)");
          setAuthorized(true);
          setLoading(false);
          return;
        }

        // Vérification des correspondances d'email directes (priorité 2)
        if (user.email) {
          const userEmailLowercase = user.email.toLowerCase();
          const candidateEmailLowercase = feedback.candidate_email ? feedback.candidate_email.toLowerCase() : null;
          const recruiterEmailLowercase = feedback.recruiter_email ? feedback.recruiter_email.toLowerCase() : null;
          
          console.log("Comparaison des emails:", 
            "User:", userEmailLowercase, 
            "Candidat:", candidateEmailLowercase, 
            "Recruteur:", recruiterEmailLowercase);
          
          if ((candidateEmailLowercase && candidateEmailLowercase === userEmailLowercase) || 
              (recruiterEmailLowercase && recruiterEmailLowercase === userEmailLowercase)) {
            console.log("Accès accordé via correspondance d'email directe");
            setAuthorized(true);
            setLoading(false);
            return;
          }
        }

        // Vérification de l'accès via les demandes de feedback (priorité 3)
        if (user.email) {
          console.log("Vérification de l'accès via email avec fonction RPC:", user.email);
          try {
            const { data, error: reqError } = await supabase.rpc<AccessCheckResult>(
              'check_feedback_access',
              { 
                feedback_id: id,
                user_email: user.email.toLowerCase()
              }
            );

            if (reqError) {
              console.error("Erreur lors de la vérification RPC de l'accès:", reqError);
              throw reqError;
            } 
            
            console.log("Résultat de la vérification RPC:", data);
            
            if (data && data.has_access) {
              console.log("Accès accordé via email dans feedback_requests");
              setAuthorized(true);
              setLoading(false);
              return;
            } else {
              console.log("Accès refusé via la fonction RPC check_feedback_access");
            }
          } catch (rpcError) {
            console.error("Exception lors de la vérification RPC:", rpcError);
          }
        }
        
        // Vérification supplémentaire via recherche directe dans feedback_requests
        if (user.email) {
          console.log("Recherche directe dans feedback_requests avec email:", user.email);
          const { data: requestData, error: requestError } = await supabase
            .from('feedback_requests')
            .select('feedback_id')
            .eq('feedback_id', id)
            .ilike('candidate_email', user.email)
            .maybeSingle();
            
          if (!requestError && requestData && requestData.feedback_id) {
            console.log("Accès accordé via recherche directe dans feedback_requests");
            setAuthorized(true);
            setLoading(false);
            return;
          }
          
          // Vérifier également si l'utilisateur est un recruteur
          const { data: recruiterRequestData, error: recruiterRequestError } = await supabase
            .from('feedback_requests')
            .select('feedback_id')
            .eq('feedback_id', id)
            .ilike('recruiter_email', user.email)
            .maybeSingle();
            
          if (!recruiterRequestError && recruiterRequestData && recruiterRequestData.feedback_id) {
            console.log("Accès accordé via recherche directe dans feedback_requests (en tant que recruteur)");
            setAuthorized(true);
            setLoading(false);
            return;
          }
        }
        
        // Si on arrive ici, l'utilisateur n'est pas autorisé
        console.log("Accès refusé: aucune méthode n'a validé l'accès");
        setAuthorized(false);
        toast({
          title: "Accès refusé",
          description: "Vous n'avez pas les permissions nécessaires pour accéder à ce feedback",
          variant: "destructive",
        });
        navigate('/dashboard');
      } catch (error) {
        console.error("Erreur lors de la vérification de l'accès au feedback:", error);
        toast({
          title: "Erreur",
          description: "Une erreur est survenue lors de la vérification des accès",
          variant: "destructive",
        });
        setAuthorized(false);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    checkFeedbackAccess();
  }, [id, user, profile, navigate, toast]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-gray-50">
        {!loading && <FeedbackView allowAccess={authorized} />}
        {loading && <LoadingSpinner />}
      </main>
      <Footer />
    </div>
  );
};

export default ViewFeedback;
